import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { Toaster } from '@tokeet/tv3-platform'
import { Message, MessageType } from '@tv3/store/message/message.model'
import { MessageRequest } from '@tv3/interfaces/requests/message.request'
import { MessageResponse } from '@tv3/interfaces/responses/message.response'
import { deserializeArray } from '@tokeet/tv3-platform'
import * as lodash from 'lodash'
import * as moment from 'moment'
import { AuthService } from '@tv3/services/auth.service'
import { isUUIDLose, userFullname } from '@tokeet-frontend/utility'

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  constructor(private http: HttpClient, private toast: Toaster, private auth: AuthService) {}

  getMessagesByInquiryId(inquiryId: string): Observable<Message[]> {
    const url = `@api/message/inquiry/${inquiryId}?include_draft=1`
    return this.http.get(url).pipe(deserializeArray<Message>(Message))
  }

  getLatest(type?: MessageType): Observable<Message[]> {
    const url = `@api/message/latest`

    return this.http.get(url, { params: { type: type as any } }).pipe(deserializeArray<Message>(Message))
  }

  create(request: MessageRequest): Observable<Message> {
    const url = `@api/message/`

    return this.http.post<object>(url, request).pipe(
      // when sending message to ABB, no message returned, create one from payload
      map((response) =>
        Message.deserialize({
          ...request,
          type: MessageType.Outbound,
          name: userFullname(this.auth.user),
          received_on: moment().unix(),
          created: moment().unix(),
          pkey: btoa(new Date().toISOString()),
          ...response,
        })
      ),
      map((message) => {
        message.sentWith = 'tokeet'
        return message
      }),
      tap(() => this.toast.success('Message sent successfully!'))
      // catchError(() => throwError({ error: 'Unable to save message' }))
    )
  }

  remove(message: Message, silent = false): Observable<{ messageId: string; inquiryId: string }> {
    const url = `@api/message/delete/${message.id}`

    return this.http.delete<MessageResponse>(url).pipe(
      map((r) => ({ messageId: r.pkey, inquiryId: r.inquiry_id })),
      tap(() => !silent && this.toast.success('Message deleted successfully!'))
    )
  }

  removeFromFeed(message: Message) {
    const url = `@api/message/remove/feed/${message.id}`

    return this.http.delete<MessageResponse>(url).pipe(
      tap(() => this.toast.success('Message removed successfully')),
      catchError(() => throwError({ error: 'Unable to remove message' }))
    )
  }

  markAsRead(messageIds: string[]) {
    messageIds = lodash.compact(messageIds).filter((id) => isUUIDLose(id))
    if (!messageIds.length) {
      return of([])
    }
    const url = `@api/message/read/all`

    return this.http.put(url, { messages: lodash.compact(messageIds) })
  }

  move(messageId: string, inquiryId: string) {
    const url = `@api/message/move/${messageId}/${inquiryId}`

    return this.http.put(url, {}).pipe(tap(() => this.toast.success('Message moved successfully!')))
  }
}
